import React, { useState } from 'react'
import find from 'lodash/find'
import filter from 'lodash/filter'
import snakeCase from 'lodash/snakeCase'
import { domToReact } from 'html-react-parser'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ImageButton from 'tcweb-material-components/core/imageButton'
import Dialog from 'tcweb-material-components/core/dialog'
import map from 'tcweb-material-components/core/poly/map'
import get from 'tcweb-material-components/core/poly/get'
import useZoomPercentage from 'tcweb-material-components/core/hooks/useZoomPercentage'

import { useTranslation } from 'react-i18next'
import classnames from 'classnames'

import ImageCarousel from '../../components/common/imageCarousel'

const MOBILE_COLS = 2

const figcaptionStyles = (spacing, palette) => ({
    position: 'absolute',
    pointerEvents: 'none',
    bottom: 0,
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    padding: spacing(2),
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    textAlign: 'center',
    color: `${palette.common.white} !important`, // Overwrite gutenberg.scss
    background: `linear-gradient(0deg, ${fade(palette.common.black, 0.8)}, ${fade(palette.common.black, 0.4)} 60%, transparent)`
})

const useClasses = makeStyles(({ palette, spacing, breakpoints, zIndex }) => ({
    grid: {
        display: 'grid',
        gridGap: '1em',
        gridTemplateColumns: `repeat(${MOBILE_COLS}, 1fr)`,
        [breakpoints.up('md')]: { gridTemplateColumns: ({ numCols }) => `repeat(${numCols}, 1fr)` }
    },
    gridDefaultWidth: {
        width: '100%'
    },
    item: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        alignItems: 'center',

        '& figure, & a, & a > img': { width: '100%' },
        '& img': { height: 'auto' },
        '& figure, & a': {
            display: 'flex',
            '& * ': {
                width: '100%',
                height: '100%'
            }
        }
    },
    isCropped: {
        '& img': { objectFit: 'cover', height: '100%', width: '100%'},
        '& figure': { height: '100%', width: '100%' }
    },
    imgButtonCropped: {
        width: '100%', height: '100%',
        '& *': { width: '100%', height: '100%' }
    },
    rootCaptionOverlay: {
        '& figure': {
            position: 'relative',

            '& figcaption': {
                ...figcaptionStyles(spacing, palette)
            }
        }
    },
    captionOverlay: {
        ...figcaptionStyles(spacing, palette)
    },
    captionUnder: {
        paddingTop: `${spacing(.5)}`
    },

    carouselRoot: {
        display: 'grid',
        [breakpoints.up('md')]: {
            height: '90%'
        }
    },
    carouselStepper: { marginTop: '1em', marginBottom: '1em' },
    carouselSlideContainer: {
        justifyContent: 'center',
        [breakpoints.up('md')]: { height: '100%' }
     },
    dialogPaper: {
        display: 'grid',
        gridTemplateRows: `1fr minmax(50%, 100%)`,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    imageContainer: { height: '100%' },
    imgItem: {
        [breakpoints.up('md')]: {
            height: '100%',
            objectFit: 'contain'
        }
    },
    stepButton: {
        display: 'none',
        [breakpoints.up('md')]: { display: 'flex' }
    },
    stepButtonZoomed: {
        display: 'flex',
        alignSelf: 'flex-end',
        marginBottom: '.4em'
    },
    downloadButton: {
        [breakpoints.up('md')]: {
            width: '25%',
            marginLeft: 'auto',
            marginRight: 'auto'
        }
    }
}))

const deepFindType = (elem, type) => {
    // Recursively look for item
    const children = get(elem, 'children', [])
    for(let i in children){
        if(children[i].name === type) return children[i]
        else{
            const foundElem = deepFindType(children[i], type)
            if(foundElem) return foundElem
        }
    }

    // Couldn't find it
    return false
}

const deepFindData = (elem) => {
    return find(elem.children, (c) => {
        if(c => !!c.data) return c
        else { deepFindData(c) }
    })
}

const GalleryCarousel = ({isCropped, numCols, items, captionOrientation}) => {
    const { t } = useTranslation()
    const _classes = useClasses({ numCols })
    const [open, setOpen] = useState(false)
    const zoomPercentage = useZoomPercentage()
    const [initialIndex, setInitialIndex] = useState(0)
    const [activeStep, setActiveStep] = useState(null)
    const _activeStep = !!activeStep ? activeStep : initialIndex
    const currentTitle = items[_activeStep].label

    const handleClose = () => setOpen(false)
    const handleToggle = (i) => {
        setOpen(!open)
        setInitialIndex(i)
    }

    return (
        <>
            {
                map((child, i) => (
                    <div key={i} className={_classes.item}>
                        <ImageButton
                            classes={{ root: classnames({[_classes.imgButtonCropped]: isCropped}) }}
                            src={child.imgPath}
                            alt={child.alt}
                            ImageProps={child.extra}
                            onClick={() => { handleToggle(i) }}
                            aria-label={child.label}
                            imageChildren={captionOrientation === 'overlay' && !!child.label ? <Typography className={classnames('h6', _classes.captionOverlay)}>{child.label}</Typography> : <></>}
                        />
                        {captionOrientation === 'under' && <Typography className={classnames('h6', _classes.captionUnder)}>{child.label}</Typography>}
                    </div>
                ), items)
            }
            <Dialog
                color='tertiary'
                open={open}
                onClose={handleClose}
                fullScreen
                title={currentTitle}
                PaperProps={{className: _classes.dialogPaper}}
                labelledBy={snakeCase(currentTitle)}
                CloseButtonProps={{
                    'aria-label': t('Close')
                }}
            >
                <ImageCarousel
                    classes={{
                        root: _classes.carouselRoot,
                        stepper: _classes.carouselStepper,
                        slideContainer: _classes.carouselSlideContainer,
                        stepButtonPrev: classnames(_classes.stepButton, {[_classes.stepButtonZoomed]: zoomPercentage > 200}),
                        stepButtonNext: classnames(_classes.stepButton, {[_classes.stepButtonZoomed]: zoomPercentage > 200})
                    }}
                    initialStep={initialIndex}
                    items={items}
                    itemProps={{className: _classes.imageContainer}}
                    onChange={(activeStep) => {setActiveStep(activeStep)}}
                    swipeableStyles={{ height: '100%' }}
                    backButtonProps={{
                        title: t('Previous image'),
                        'aria-label': t('Previous image')
                    }}
                    nextButtonProps={{
                        title: t('Next image'),
                        'aria-label': t('Next image')
                    }}
                    showLabel={false}
                />
            </Dialog>
        </>
    )
}

const getGalleryItems = (children, _classes) => map(child => {
    const isImg = get(child, 'name') === 'figure'
    const figureElem = isImg ? child : deepFindType(child, 'figure')
    const imgElem = deepFindType(figureElem, 'img')
    const captionElem = deepFindType(figureElem, 'figcaption')

    return {
        className: _classes.imgItem,
        label: get(deepFindData(captionElem), 'data', ''),
        alt: get(imgElem, 'attribs.alt'),
        imgPath: get(imgElem, 'attribs.src', get(figureElem, 'attribs.src', '')),
        extra: {
            width: get(imgElem, 'attribs.width'),
            height: get(imgElem, 'attribs.height'),
            loading: get(imgElem, '.attribs.loading')
        }
    }
}, children)

const GallerySwap = ({ attribs, children, className, options }) => {
    // Get cols from classNmae
    const classNames = get(attribs, 'class', '').split(' ')
    const columnsClass = find(classNames, (c) => c.startsWith('columns-'))
    const colsRegex = columnsClass.match(/(\d+)/)
    const numCols = !!colsRegex ? Math.min(colsRegex[0], 12) : 1
    const captionOrientation = get(attribs, 'data-caption-orientation')

    const _classes = useClasses({ numCols })
    const hasCarouselStr = get(attribs, 'data-has-carousel')
    const hasCarousel = !!hasCarouselStr && JSON.parse(hasCarouselStr.toLowerCase()) === true
    const _className = !!className ? className.replace('wp-block-gallery', '') : ''

    // Get isCropped
    const isCropped = attribs.class.includes('is-cropped')

    const galleryElements = filter(children, (c) => get(c, 'name', '') === 'figure')
    const caption = get(deepFindData(children), 'data', '')

    return (
        <>
            <figure className={classnames(
                _className,
                _classes.grid,
                {
                    [_classes.isCropped]: isCropped,
                    [_classes.rootCaptionOverlay]: captionOrientation === 'overlay',
                    [_classes.gridDefaultWidth]: !classNames.includes('alignwide') || !classNames.includes('alignfull')
                }
            )}>
            {
                hasCarousel ?
                    <GalleryCarousel isCropped={isCropped} items={getGalleryItems(galleryElements, _classes)} numCols={numCols} captionOrientation={captionOrientation} />
                    :
                    map((child, i) => {
                        return (
                            <div key={i} className={classnames(_classes.item, 'h6')}>
                                {domToReact(child.children, options)}
                            </div>
                        )
                    }, galleryElements)

            }
            </figure>
            <figcaption className='blocks-gallery-caption'>{caption}</figcaption>
        </>
    )
}

export default GallerySwap

